/* @flow */
/*
 *  Copyright 2018 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React, {Component} from 'react';
import Alert from '@react/react-spectrum/Icon/Alert';
import {productData, analytics, singleOSSupportedApps} from '../../../constants/ccdConstants';
import CCDUtil from '../../../utils/CCDUtil';
import AppsUtil from '../../../utils/AppsUtil';
import CompatibilityUtil from '../../../utils/CompatibilityUtil';
import Url from '../../../utils/Url';
import './ErrorState.scss';
import CCDIngestAnalytics from '../../../analytics/CCDIngestAnalytics';
import {errorData, getContextGuid, errorClickData} from '../../../utils/CCDAnalyticsUtils';
import Link from '@react/react-spectrum/Link';

type Props = {
	errorType: string,
	routeParams: Object,
	environment: string,
	locale: string,
	intl: Object,
	hasThor: boolean
};

export default class ErrorState extends Component<Props> {
	componentDidMount() {
		CCDUtil.hideFooter();
		if (this.props.errorType === 'pageNotFound') {
			this.sendAnalytic(analytics.pageNotFoundError);
		}
		else {
			this.sendAnalytic(analytics.errorPageRendered);
		}
	}

	sendAnalytic(type: string) {
		const ccdIngestAnalytics = new CCDIngestAnalytics(this.props.environment);
		ccdIngestAnalytics.initAnalytics();

		let data = {};
		let outcome = false;
		if (this.props.errorType === 'os' && this.props.routeParams.productId === 'lightroom' && !Url.getParam('purchaseLrccRedirect')) {
			outcome = `false-lr-cc/${(!CompatibilityUtil.isOSDeprecated()).toString()}-older-lr-classic`;
		}
		switch (type) {
			case 'pageNotFoundError':
				data = CCDUtil.getCloneObject(errorData);
				break;
			case 'assetUrlLinkClicked' :
				data = CCDUtil.getCloneObject(errorClickData);
				data.subType = 'your-files';
				break;
			case 'serviceUrlLinkClicked' :
				data = CCDUtil.getCloneObject(errorClickData);
				data.subType = 'your-apps-services';
				break;
			case 'errorPageRendered':
				data = CCDUtil.getCloneObject(errorData);
				break;
			case 'systemRequirementsClicked' :
				data = CCDUtil.getCloneObject(errorClickData);
				data.subType = 'system-requirements';
				break;
			case 'lightroomClassicLinkClicked' :
				data = CCDUtil.getCloneObject(errorClickData);
				data.subType = 'lightroom-classic';
				break;
			case 'learnMoreClicked' :
				data = CCDUtil.getCloneObject(errorClickData);
				data.subType = 'learn-more';
				break;
		}
		data.errorType = this.props.errorType === 'pageNotFound' ? 'Wrong Product URL' : 'O/S deprecated';
		if (this.props.errorType === 'service_down') {
			data.errorType = 'FFC not reachable';
		}
		data.productId = this.props.routeParams.productId;
		data.componentName = this.props.hasThor ? 'Thor' : 'Kaizen';
		data.contextGuid = this.props.errorType !== 'pageNotFound' ? getContextGuid(true, this.props.hasThor, outcome) : null;
		ccdIngestAnalytics.genericAnalyticEvent(data);
	}

	// Error messages for different type of errors
	getMessage(type: string) {
		switch (type) {
			case 'os':
				return singleOSSupportedApps.includes(this.props.routeParams.productId)
					? this.props.intl.formatMessage({id: 'ccd.common.errorMessage.singleOSApp.description'}, {appName:productData[this.props.routeParams.productId].name})
					: this.props.intl.formatMessage({id: 'ccd.common.errorMessage.description'}, {appName:productData[this.props.routeParams.productId].name});
			case 'lightroom-os':
				return ([
					<p key='msg1'>{this.props.intl.formatMessage({id: 'ccd.common.errorMessage.description1'}, {appName:productData[this.props.routeParams.productId].name})}</p>,
					<p key='msg2'>{this.props.intl.formatMessage({id: 'ccd.common.errorMessage.description2'}, {appName:productData['lightroom-classic'].name})}<br/>
						<a key='link1' href='/apps/download/lightroom-classic' target='_blank' onClick={() => this.sendAnalytic(analytics.lightroomClassicLinkClicked)} >{this.props.intl.formatMessage({id: 'ccd.common.downloadApp'},{appName:productData['lightroom-classic'].name})}</a>
						<span key='separator' className='separator'>|</span>
						<a key='link2' target='_blank' href={CCDUtil.getLightroomLearnMoreURL(this.props.locale)} onClick={() => this.sendAnalytic(analytics.learnMoreClicked)} >{this.props.intl.formatMessage({id: 'ccd.common.learnMore'})}</a></p>,
					<p key='msg3' id='lightroomMessage3'>{this.props.intl.formatMessage({id: 'ccd.common.errorMessage.description3'},{appName:productData[this.props.routeParams.productId].name})}</p>
				]);
			default:
				return this.props.intl.formatMessage({id: 'ccd.old.common.errorMessage'}, {
					a: str => <Link href={AppsUtil.getCCDGoURL(this.props.locale, CCDUtil.getCustomerSupportURL())} target='_blank'>{str}</Link>
				});
		}
	}

	isLightroomSpecialErrorMessageCase() {
		return this.props.errorType === 'os' && this.props.routeParams.productId === 'lightroom' && !Url.getParam('purchaseLrccRedirect') && !CompatibilityUtil.isOSDeprecated();
	}

	getErrorMsg() {
		if (this.isLightroomSpecialErrorMessageCase()) {
			return <div className='error-description' >{this.getMessage('lightroom-os')}</div>;
		}
		return <div className='error-description'>{this.getMessage(this.props.errorType)}</div>;
	}

	getTitle() {
		return productData[this.props.routeParams.productId].name;
	}

	// render page not found error or other os errors.
	render() {
		let domArr = [];
		if (this.props.errorType === 'pageNotFound') {
			domArr = [
				<div key='page-not-found' className='page_error'>
					<div className='error-wrap'>
						<div className='inner'>
							<div className='cclogo'></div>
							<h1>{this.props.intl.formatMessage({id: 'ccd.old.pageNotFound.title'})}</h1>
							<p> {this.props.intl.formatMessage({id: 'ccd.old.pageNotFound.desc'}, {
								br: () => <br/>,
								a1: str => <Link href='https://assets.adobe.com/files' target='_blank' onClick={() => this.sendAnalytic(analytics.assetUrlLinkClicked)}>{str}</Link>,
								a2: str => <Link href='https://www.adobe.com/creativecloud/catalog/desktop.html' target='_blank' onClick={() => this.sendAnalytic(analytics.serviceUrlLinkClicked)}>{str}</Link>
							})}
							</p>
						</div>
					</div>
				</div>];
		}
		else {
			domArr = [
				<div key='error-container' className='error_container'>
					<p className='header'><span>{this.props.intl.formatMessage({id: 'ccd.common.errorMessage.heading'}, {appName: this.isLightroomSpecialErrorMessageCase() ? '' : this.getTitle()})}</span> <Alert className='alert-icon' size='S' /></p>
					{this.getErrorMsg()}
					<a target='_blank' href={AppsUtil.getCCDGoURL(this.props.locale, AppsUtil.getSystemReqURL(this.props.routeParams.productId))} onClick={() => this.sendAnalytic(analytics.systemRequirementsClicked)}>{this.props.intl.formatMessage({id: 'ccd.common.seeSystemReq'})}</a>
				</div>
			];
		}
		return (
			domArr
		);
	}
};
